import { useSignUpPasswordless } from "Api/Mutations/Auth/useSignUpPasswordless";
import { useScanPayPoint } from "Api/Mutations/PayPoints/useScanPayPoint";
import { useBankingAccounts } from "Api/Queries/Accounts/useBankingAccounts";
import {
  PAYMENT_STEP_URL_PARAMS,
  type PaymentStep,
} from "Components/AnonymousCustomer/Payment/config";
import { useLoggedInUser } from "Hooks/useLoggedInUser";
import { UserStatus } from "Infrastructure/Api/Api";
import { useNavigate } from "Navigation/useNavigate";
import { Resources, useResource } from "Translations/Resources";
import {
  type ErrorType,
  getTransactionErrorMessage,
} from "Utils/TransactionErrorsUtils";
import { appUrl } from "Utils/UrlUtils";
import { useState } from "react";

type StartPaymentParams = {
  publicID: string;
  amount: number;
  tipAmount?: number;
};

/**
 * Orchestrates the anonymous collector payment start: scans the PayPoint to
 * create a transaction, ensures the caller has an (anonymous) user, and routes
 * to the right payment step — account selection when the returning anonymous
 * user already has a connected bank, bank connection otherwise.
 */
export const useStartCollectorPayment = () => {
  const { t } = useResource();
  const navigate = useNavigate();
  const user = useLoggedInUser();
  const [errorMessage, setErrorMessage] = useState<string | undefined>();

  // Prefetch for returning anonymous users — used to decide whether the bank
  // connection step can be skipped. Disabled (no 401) when there is no JWT yet.
  const {
    data: bankingAccounts,
    isLoading: isLoadingBankingAccounts,
    refetch: refetchBankingAccounts,
  } = useBankingAccounts(undefined, !!user);
  const { mutate: signUpPasswordless, isPending: isSigningUp } =
    useSignUpPasswordless();

  const { mutate: scanPayPoint, isPending: isScanning } = useScanPayPoint({
    onError: (error) =>
      setErrorMessage(
        getTransactionErrorMessage(error as unknown as ErrorType, t),
      ),
    onSuccess: async ({ transactionPublicID }) => {
      const goToStep = (step: Exclude<PaymentStep, "detail">) =>
        navigate(
          `${appUrl("customer-payment-pay")}/${encodeURIComponent(transactionPublicID)}?step=${PAYMENT_STEP_URL_PARAMS[step]}`,
        );

      if (user?.userStatus === UserStatus.Anonymous) {
        // JWT exists here
        let accountsData = bankingAccounts;
        if (isLoadingBankingAccounts) {
          const result = await refetchBankingAccounts();
          accountsData = result.data;
        }
        goToStep(accountsData?.items.length ? "account" : "bank");
      } else {
        signUpPasswordless(
          { transactionPublicID },
          {
            onSuccess: () => goToStep("bank"),
            onError: () =>
              setErrorMessage(t(Resources.TransactionErrors.UnknownError)),
          },
        );
      }
    },
  });

  const startPayment = (params: StartPaymentParams) => {
    setErrorMessage(undefined);
    scanPayPoint(params);
  };

  return {
    startPayment,
    isProcessing: isScanning || isSigningUp,
    errorMessage,
  };
};
